// Capacitor native integration — additive and web-safe.
// Every call is guarded by Capacitor.isNativePlatform(), so importing this
// module and calling its functions are no-ops in the browser build.

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { Toast } from '@capacitor/toast';
import { SplashScreen } from '@capacitor/splash-screen';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

const isNative = () => Capacitor.isNativePlatform();

let _initialized = false;

/**
 * One-time setup of native listeners/config: status bar, keyboard resize mode,
 * and the hardware back button. Safe to call from index.js at startup.
 */
export function initNative() {
  if (!isNative() || _initialized) return;
  _initialized = true;

  // Status bar: dark icons on the app's light (#ffffff) theme.
  // (Style.Light = dark text/icons for light backgrounds.)
  StatusBar.setStyle({ style: Style.Light }).catch(() => {});
  if (Capacitor.getPlatform() === 'android') {
    StatusBar.setBackgroundColor({ color: '#ffffff' }).catch(() => {});
  }

  // Keyboard: resize the body so inputs scroll above the keyboard instead of
  // being obscured. `Body` is correct for a non-Ionic React app.
  Keyboard.setResizeMode({ mode: KeyboardResize.Body }).catch(() => {});

  // Hardware back button: navigate back in history; at a root route use
  // double-tap-to-exit (avoids the janky single-press instant exit).
  let exitPending = false;
  App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack || window.history.length > 1) {
      window.history.back();
      return;
    }
    if (exitPending) {
      App.exitApp();
      return;
    }
    exitPending = true;
    Toast.show({ text: 'Press back again to exit', duration: 'short' }).catch(() => {});
    setTimeout(() => { exitPending = false; }, 2000);
  });
}

/**
 * Hide the splash screen. Call AFTER first paint (from a root useEffect), not
 * synchronously at module import — with launchAutoHide:false an early hide
 * would reveal a blank white screen until React paints.
 */
export function confirmSplashHidden() {
  if (!isNative()) return;
  SplashScreen.hide().catch(() => {});
}

/**
 * Tell Capgo the bundle booted to a known-good state so it isn't rolled back.
 * Call once the initial auth state has resolved (from AppRouter), NOT at React
 * mount — a bundle that mounts but crashes on auth/first route should still
 * roll back. Must fire within CapacitorUpdater.appReadyTimeout.
 */
export function confirmAppReady() {
  if (!isNative()) return;
  CapacitorUpdater.notifyAppReady().catch(() => {});
}
